<script setup lang="ts">
  import useHeaderDataLayer from '@/composables/data-layers/useHeaderDataLayer'
  import { useLanguagesStore } from '@/stores/Languages.store'
  import { useLocaleStore } from '@/stores/Locale.store'
  import { useCountriesStore } from '@/stores/Countries.store'
  import { usePageStore } from '@/stores/Page.store'
  import { useDynamicTranslation } from '@/composables/useDynamicTranslation'
  import { storeToRefs } from 'pinia'

  const { locale } = useI18n()
  const { translate } = useDynamicTranslation()
  const switchLocalePath = useSwitchLocalePath()
  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()

  const languageStore = useLanguagesStore()
  const localeStore = useLocaleStore()
  const countryStore = useCountriesStore()
  const pageStore = usePageStore()

  const { availableLanguages } = storeToRefs(languageStore)
  const { selectedLanguage, selectedCountry, selectedCountrySameAsCookie } = storeToRefs(localeStore)
  const { availableCountries } = storeToRefs(countryStore)

  countryStore.getCountriesFromAPI()

  const isLanguageExternal = (languageId: string) => {
    return availableLanguages.value.find((item) => item.id === languageId)?.external
  }

  const isPopoverOpen = ref(false)
  const isCountrySelectOpen = ref(false)
  const isLanguageSelectOpen = ref(false)

  const isSelectOpen = computed(() => isCountrySelectOpen.value || isLanguageSelectOpen.value)

  const { pushHeaderLanguageChangeDataLayer } = useHeaderDataLayer()

  const setLocalization = async () => {
    if (!selectedCountrySameAsCookie.value) {
      localeStore.setSelectedCountry(selectedCountry.value)
      isPopoverOpen.value = false
    }

    if (locale.value === selectedLanguage.value) {
      isPopoverOpen.value = false

      return
    }

    pushHeaderLanguageChangeDataLayer(selectedLanguage.value)

    const external = isLanguageExternal(selectedLanguage.value)

    if (external) {
      return navigateTo(external, { external: true })
    }

    const langPath = pageStore.slugs?.filter((slug) => slug.lang === selectedLanguage.value)
    const [pagePath] = langPath || []

    const path = pagePath?.path
      ? `/${selectedLanguage.value}/${pagePath.path}`
      : switchLocalePath(selectedLanguage.value)

    const urlBase = runtimeConfig.public.siteUrl
    const targetUrl = new URL(path, urlBase)
    const currentUrl = new URL(route.fullPath, urlBase)

    targetUrl.search = currentUrl.search
    targetUrl.hash = currentUrl.hash

    reloadNuxtApp({ path: `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`, force: true })
  }

  const resetUserSelection = (value: boolean | undefined) => {
    if (!value) {
      localeStore.resetSelectedLanguageCountry()
    }
  }

  const saveButtonTitle = ref<string>('')

  watch(
    () => selectedLanguage.value,
    async () => {
      saveButtonTitle.value = await translate('save')
    },
    { immediate: true }
  )
</script>

<template>
  <div>
    <UiPopover
      v-model="isPopoverOpen"
      has-overlay
      :label="$t('language.select')"
      @update:model-value="resetUserSelection"
    >
      <template #trigger>
        <div class="inline-flex size-10 cursor-pointer items-center justify-center rounded-full bg-white">
          <Icon name="common/language" />
        </div>
      </template>
      <template #content>
        <div
          class="fixed z-10 flex h-full w-full flex-col gap-5 bg-white p-5 pt-20 sm:relative sm:h-auto sm:min-w-90 sm:rounded sm:pt-5"
        >
          <UiSelect
            v-model="selectedCountry"
            v-model:open="isCountrySelectOpen"
            label="country.select"
            disable-item-translation
            name="Country"
            :items="availableCountries"
          />

          <UiSelect
            v-model="selectedLanguage"
            v-model:open="isLanguageSelectOpen"
            label="language.select"
            disable-item-translation
            name="Langauge"
            :items="availableLanguages"
          />
          <div>
            <LazyUiButton
              class="w-full font-medium"
              :title="saveButtonTitle"
              variant="primary"
              @click="setLocalization"
            />
          </div>
        </div>
      </template>
      <template #close>
        <button
          class="absolute left-5 top-5 z-10 flex size-6 cursor-pointer"
          :aria-label="$t('aria.label.closeLanguageMenuButton')"
          :class="{ hidden: isSelectOpen }"
        >
          <Icon name="common/close" />
        </button>
        <div
          class="absolute left-0 top-0 h-16 w-full border-b bg-white"
          :class="{ hidden: isSelectOpen }"
        />
      </template>
    </UiPopover>
    <UiDarkOverlay
      v-if="isPopoverOpen"
      class="inset-0"
    />
  </div>
</template>
